import type { Language } from "./localization";
import type { ModelRelease, Organization } from "../types/model";

export type OrganizationIndex = ReadonlyMap<string, Organization>;

export function buildOrganizationIndex(
  organizations: Organization[]
): OrganizationIndex {
  return new Map(organizations.map((organization) => [organization.id, organization]));
}

export function organizationName(
  organizationId: string,
  organizations: OrganizationIndex,
  language: Language
): string {
  const organization = organizations.get(organizationId);
  return organization?.names[language] ?? organizationId;
}

export function organizationGroupIds(
  record: Pick<ModelRelease, "organization">
): string[] {
  return record.organization.filterIds.length
    ? record.organization.filterIds
    : [record.organization.groupId];
}

export function organizationHeadingIds(
  record: Pick<ModelRelease, "organization">
): string[] {
  return record.organization.homepageOrganizationIds?.length
    ? record.organization.homepageOrganizationIds
    : organizationGroupIds(record);
}

export function organizationGroupKey(
  record: Pick<ModelRelease, "organization">
): string {
  return JSON.stringify(organizationGroupIds(record));
}

export function organizationAttributionLabel(
  organizationIds: string[],
  organizations: OrganizationIndex,
  language: Language
): string {
  return organizationIds
    .map((id) => organizationName(id, organizations, language))
    .join(" × ");
}

export function primaryOrganization(
  record: ModelRelease,
  organizations: OrganizationIndex
): Organization | undefined {
  return organizations.get(record.organization.primaryId);
}

// The company keys whose artwork a model card may fall back to, nearest first:
// the organization itself, then each `parentId` ancestor in turn. A card only
// shows initials when none of them resolves to a logo.
export function organizationLogoCompanies(
  organizationId: string,
  organizations: OrganizationIndex
): string[] {
  const companies: string[] = [];
  const visited = new Set<string>();
  let organization = organizations.get(organizationId);

  while (organization && !visited.has(organization.id)) {
    visited.add(organization.id);
    companies.push(organization.logoCompany ?? organization.names.en);
    organization = organization.parentId
      ? organizations.get(organization.parentId)
      : undefined;
  }

  return companies;
}

export function groupOrganization(
  record: ModelRelease,
  organizations: OrganizationIndex
): Organization | undefined {
  return organizations.get(record.organization.groupId);
}

export function developerOrganizations(
  record: ModelRelease,
  organizations: OrganizationIndex
): Organization[] {
  return record.organization.developerIds.flatMap((id) => {
    const organization = organizations.get(id);
    return organization ? [organization] : [];
  });
}

export function developerAttributionLabel(
  record: Pick<ModelRelease, "organization" | "company">,
  organizations: OrganizationIndex,
  language: Language
): string {
  const label = organizationAttributionLabel(
    record.organization.developerIds,
    organizations,
    language
  );
  return label || record.company;
}
